import { useMemo } from 'react';

import { translate } from '@repo/phrase';
import { Heading, Paragraph } from '@repo/ui/typography';

import { formatDeliveryAddress } from '@/utils/services.utils';

import { isBusinessWithIbn, isFamilyMember } from '@sp-saljapro/ot-customer-information';
import styles from './styles.module.scss';

interface Props {
    addressLine1?: string | undefined;
    addressLine2?: string | undefined;
    businessName?: string | undefined;
    city?: string | undefined;
    countryCode?: string | undefined;
    email?: string | undefined;
    familyNumber?: string | undefined;
    firstName?: string | undefined;
    isBusinessCustomer?: boolean | undefined;
    lastName?: string | undefined;
    mobile?: string | undefined;
    stateCode?: string | undefined;
    zipCode?: string | undefined;
    isIbn?: boolean | undefined;
}

export const CustomerCard = ({
    addressLine1,
    addressLine2,
    businessName,
    city,
    countryCode,
    email,
    familyNumber,
    firstName,
    isBusinessCustomer,
    lastName,
    mobile,
    stateCode,
    zipCode,
    isIbn,
}: Props) => {
    const firstAndLastName = useMemo(() => {
        if (firstName && lastName) {
            return translate('order_sidebar_customer_fullname', {
                firstName,
                lastName,
            });
        }

        return null;
    }, [firstName, lastName]);

    if (!firstName && !lastName && !email && !mobile && !addressLine1 && !city && !zipCode && !businessName) {
        return null;
    }

    const displayedAddress = formatDeliveryAddress({
        addressLine1,
        addressLine2,
        city,
        zipCode,
        stateCode,
        countryCode,
    });

    // If isIbn flag is provided (drafts & quotes), use it.
    // If not (orders) determine if the customer is IBN based on family number and business status.
    const isIbnBusinessCustomer = isIbn ?? isBusinessWithIbn(isBusinessCustomer, familyNumber);
    const isCustomerFamilyMember = isFamilyMember(familyNumber, isBusinessCustomer);
    const isLabelRequired = isCustomerFamilyMember || isIbnBusinessCustomer;

    const customerLabel = isLabelRequired
        ? isIbnBusinessCustomer
            ? 'order_sidebar_business_network'
            : 'order_sidebar_family_member'
        : undefined;

    const renderHeading = () => {
        const headingContent = businessName ?? firstAndLastName;

        return (
            <>
                {headingContent && <Heading className={String(styles.customer_name)}>{headingContent}</Heading>}
                {Boolean(businessName && firstAndLastName) && <Paragraph>{firstAndLastName}</Paragraph>}
            </>
        );
    };

    return (
        <div className={styles.order_details_customer_card} data-testid="customer-card">
            {renderHeading()}
            {email && <Paragraph>{email}</Paragraph>}
            {mobile && <Paragraph>{mobile}</Paragraph>}
            {displayedAddress && <Paragraph>{displayedAddress}</Paragraph>}
            {customerLabel && (
                <Paragraph bodySize="s" className={String(styles.family_member)}>
                    {translate(customerLabel)}
                </Paragraph>
            )}
        </div>
    );
};
